import React, { useState } from 'react';
import { Box, Typography, Paper, Button, Alert, Stack } from '@mui/material';
import { CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import MarkdownRenderer from '../Common/MarkdownRenderer';

const Example = ({ example, index, hideTitle = false }) => {
  const [showAnswer, setShowAnswer] = useState(false);
  
  if (!example) {
    return null;
  }

  const { 
    question_type, 
    question, 
    answer, 
    explanation, 
    proof, 
    options, 
    correct_answer, 
    code_answer,
    blanks,
    public_tests,
  } = example;

  const renderExample = () => {
    switch (question_type) {
      case 'multiple_choice':
        return (
          <Box>
            {question && (
              <Box sx={{ mb: 2 }}>
                <MarkdownRenderer content={question} />
              </Box>
            )}
            {options && (
              <Stack spacing={1.5} sx={{ mb: 2 }}>
                {options.map((option, idx) => {
                  const isCorrect = idx === (correct_answer?.charCodeAt(0) - 'A'.charCodeAt(0));
                  return (
                    <Box
                      key={idx}
                      sx={{
                        p: 2,
                        border: 2,
                        borderColor: showAnswer && isCorrect ? 'success.main' : 'divider',
                        borderRadius: 2,
                        bgcolor: showAnswer && isCorrect ? 'success.light' : 'transparent',
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        {showAnswer && isCorrect ? (
                          <CheckCircle color="success" sx={{ mt: 0.5, flexShrink: 0 }} />
                        ) : (
                          <RadioButtonUnchecked sx={{ mt: 0.5, flexShrink: 0 }} />
                        )}
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <MarkdownRenderer content={option?.trim() ?? ''} />
                        </Box>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        );
      
      case 'code':
        return (
          <Box>
            {question && (
              <Box sx={{ mb: 2 }}>
                <MarkdownRenderer content={question} />
              </Box>
            )}
            {code_answer && (
              <Box sx={{ 
                my: 2,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                backgroundColor: '#1a1a1a',
              }}>
                <SyntaxHighlighter 
                  language="python" 
                  style={vscDarkPlus}
                  customStyle={{
                    margin: 0,
                    padding: '16px',
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    borderRadius: '8px',
                    backgroundColor: '#1a1a1a',
                  }}
                >
                  {code_answer
                    .replace(/^```python\n?/gim, '')  // 移除开头的 ```python
                    .replace(/```\s*$/gim, '')        // 移除结尾的 ```
                    .trim()}
                </SyntaxHighlighter>
              </Box>
            )}
            {showAnswer && public_tests && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  公开测试用例：
                </Typography>
                <Box sx={{ 
                  mt: 1,
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: '#1a1a1a',
                }}>
                  <SyntaxHighlighter 
                    language="python" 
                    style={vscDarkPlus}
                    customStyle={{
                      margin: 0,
                      padding: '16px',
                      fontSize: '0.875rem',
                      lineHeight: 1.6,
                      backgroundColor: '#1a1a1a',
                    }}
                  >
                    {public_tests}
                  </SyntaxHighlighter>
                </Box>
              </Alert>
            )}
          </Box>
        );
      
      case 'code_tracing':
        return (
          <Box>
            {question && (
              <Box sx={{ mb: 2 }}>
                <MarkdownRenderer content={question} />
              </Box>
            )}
            {code_answer && (
              <Box sx={{ 
                my: 2,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                backgroundColor: '#1a1a1a',
              }}>
                <SyntaxHighlighter 
                  language="python" 
                  style={vscDarkPlus}
                  customStyle={{
                    margin: 0,
                    padding: '16px',
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    borderRadius: '8px',
                    backgroundColor: '#1a1a1a',
                  }}
                >
                  {code_answer
                    .replace(/^```python\n?/gim, '')  // 移除开头的 ```python
                    .replace(/```\s*$/gim, '')        // 移除结尾的 ```
                    .trim()}
                </SyntaxHighlighter>
              </Box>
            )}
            {/* 选择题模式 */}
            {options && (
              <Stack spacing={1.5} sx={{ mb: 2 }}>
                {options.map((option, idx) => {
                  const isCorrect = idx === (correct_answer?.charCodeAt(0) - 'A'.charCodeAt(0));
                  return (
                    <Box
                      key={idx}
                      sx={{
                        p: 2,
                        border: 2,
                        borderColor: showAnswer && isCorrect ? 'success.main' : 'divider',
                        borderRadius: 2,
                        bgcolor: showAnswer && isCorrect ? 'success.light' : 'transparent',
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        {showAnswer && isCorrect ? (
                          <CheckCircle color="success" sx={{ mt: 0.5 }} />
                        ) : (
                          <RadioButtonUnchecked sx={{ mt: 0.5 }} />
                        )}
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <MarkdownRenderer content={option?.trim() ?? ''} />
                        </Box>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
            {/* 简答题模式 */}
            {!options && answer && showAnswer && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Box component="div" variant="body2">
                  <strong>参考答案：</strong>
                  <MarkdownRenderer content={answer} />
                </Box>
              </Alert>
            )}
          </Box>
        );
      
      case 'short_answer':
        return (
          <Box>
            {question && (
              <Box sx={{ mb: 2 }}>
                <MarkdownRenderer content={question} />
              </Box>
            )}
            {showAnswer && answer && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>参考答案：</strong>
                  <MarkdownRenderer content={answer} />
                </Typography>
              </Alert>
            )}
          </Box>
        );
      
      case 'proof':
        return (
          <Box>
            {question && (
              <Box sx={{ mb: 2 }}>
                <MarkdownRenderer content={question} />
              </Box>
            )}
            {showAnswer && proof && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Box component="div" variant="body2">
                  <strong>证明：</strong>
                  <MarkdownRenderer content={proof} />
                </Box>
              </Alert>
            )}
          </Box>
        );
      
      default:
        return (
          <Box>
            {question && (
              <Box sx={{ mb: 2 }}>
                <MarkdownRenderer content={question} />
              </Box>
            )}
          </Box>
        );
    }
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2.5,
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      {!hideTitle && (
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
          例题 {index !== undefined ? index + 1 : ''}
        </Typography>
      )}
      
      {renderExample()}
      
      {explanation && showAnswer && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Box component="div" variant="body2">
            <strong>解释：</strong>
            <MarkdownRenderer content={explanation} />
          </Box>
        </Alert>
      )}
      
      {(answer || proof || correct_answer || blanks || (question_type === 'code_tracing' && !options && answer)) && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowAnswer(!showAnswer)}
          >
            {showAnswer ? '隐藏答案' : '查看答案'}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default Example;
